/**
 * Contracts for the generic duplicate-group comparison engine.
 *
 * `GroupComparisonTable` renders ANY object kind (sales, products, future
 * customers/...) as member-columns × attribute-rows with diff tinting,
 * Issues chips and Ignore actions. Everything object-specific lives in a
 * `GroupComparisonSchema` (see `schemas/`): adding a new duplicate domain
 * is one schema file + one backend group endpoint - no engine changes.
 *
 * Distinct from the LS<->Xero sales-compare stack (CompareSalesView /
 * CompareSalesTable): that compares one object across two systems; this
 * compares N sibling objects within one system.
 */
import type { ReactNode } from 'react'
import type { IssuesChipEntry } from '../../../../config/exceptions/shared/issuesState'

export type ComparisonPrimitive = string | number | boolean | null | undefined

/**
 * Lifecycle fields every group member carries, stamped by the backend's
 * `enrich_group_members` (attach_row_errors under the hood). The engine
 * reads these for the Issues chips + per-member Ignore gating; schemas
 * never need to touch them.
 */
export interface ComparisonMemberBase {
  ls_id: string
  tag_id?: number | null
  /** Lifecycle status of the duplicate tag ('needs_review' | 'blocked' on
   *  the open slice). Named tag_status because sale members already use
   *  `status` for the LS sale status. */
  tag_status?: string | null
  errors?: IssuesChipEntry[]
  recently_closed_errors?: IssuesChipEntry[]
}

/** One comparison row: how to read (for diffing) and render one attribute. */
export interface ComparisonAttr<M> {
  key: string
  label: string
  /** Diff identity - members disagreeing on this value tint the row. */
  getValue: (m: M) => ComparisonPrimitive
  /** Display node; falls back to `String(getValue(m))`. */
  render?: (m: M) => ReactNode
  /** Never tint this row even when members differ (e.g. scores, images). */
  neverDiff?: boolean
  /**
   * Numeric treatment: the canonical tabular-nums stack (NUMERIC_FONT_SX -
   * same token the Compare Sales surface uses) so digits column-align.
   */
  numeric?: boolean
  /** Cell text alignment. Every data cell centers by default (the label
   *  column right-aligns against the divider); override per attribute. */
  align?: 'left' | 'right' | 'center'
}

/** Everything object-specific the engine needs for one duplicate domain. */
export interface GroupComparisonSchema<M extends ComparisonMemberBase> {
  /** Tag tuple domain for the drawer / notes anchor (e.g. 'sale'). */
  domain: string
  /** The duplicate exception code (tag tuple + chip primary). */
  exceptionCode: string
  /** Drawer heading label (e.g. 'Duplicate sale'). */
  exceptionTypeName: string
  /** Top-left header cell label (e.g. 'Sale' / 'Product'). */
  cornerLabel: string
  /** Stable member identity (= the tag tuple's object_id). */
  memberId: (m: M) => string
  /** Human identity for drawer titles + reason-dialog item lists. */
  memberTitle: (m: M) => string
  /** Member column header - identity + source links only (no actions). */
  memberHeader: (m: M) => ReactNode
  attrs: ComparisonAttr<M>[]
  /** Min member-column width (px); drives horizontal scroll. */
  minColumnWidth?: number
}

/** A member's duplicate tag is open (ignorable) in these states. */
export const isOpenMemberStatus = (
  status: string | null | undefined,
): boolean => status === 'needs_review' || status === 'blocked'
